import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import KeyboardArrowLeftOutlined from '@mui/icons-material/KeyboardArrowLeftOutlined';
import KeyboardArrowRightOutlined from '@mui/icons-material/KeyboardArrowRightOutlined';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import KeyboardArrowDownOutlined from '@mui/icons-material/KeyboardArrowDownOutlined';
import { MONTH_NAMES_PT, MONTH_ABBR_PT } from '@practium/shared';
import styles from './BillingMonthFilter.module.css';

interface BillingMonthFilterProps {
	month: number;
	year: number;
	onChange: (month: number, year: number) => void;
}

export const BillingMonthFilter = ({ month, year, onChange }: BillingMonthFilterProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [dropdownYear, setDropdownYear] = useState(year);
	const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
	const triggerRef = useRef<HTMLDivElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	const updatePosition = useCallback(() => {
		if (!triggerRef.current) return;
		const rect = triggerRef.current.getBoundingClientRect();
		setDropdownStyle({
			top: rect.bottom + 6,
			left: rect.left,
			width: rect.width,
		});
	}, []);

	useEffect(() => {
		const handleOutsideClick = (e: MouseEvent) => {
			const target = e.target as Node;
			if (
				triggerRef.current &&
				!triggerRef.current.contains(target) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(target)
			) {
				setIsOpen(false);
			}
		};
		if (isOpen) {
			updatePosition();
			document.addEventListener('mousedown', handleOutsideClick);
			document.addEventListener('scroll', updatePosition, true);
			window.addEventListener('resize', updatePosition);
		}
		return () => {
			document.removeEventListener('mousedown', handleOutsideClick);
			document.removeEventListener('scroll', updatePosition, true);
			window.removeEventListener('resize', updatePosition);
		};
	}, [isOpen, updatePosition]);

	const handleToggle = () => {
		if (!isOpen) setDropdownYear(year);
		setIsOpen((o) => !o);
	};

	const handlePrev = () => {
		if (month === 1) {
			onChange(12, year - 1);
		} else {
			onChange(month - 1, year);
		}
	};

	const handleNext = () => {
		if (month === 12) {
			onChange(1, year + 1);
		} else {
			onChange(month + 1, year);
		}
	};

	const handleMonthSelect = (monthIndex: number) => {
		onChange(monthIndex + 1, dropdownYear);
		setIsOpen(false);
	};

	const dropdown = isOpen ? (
		<div ref={dropdownRef} className={styles.dropdown} style={dropdownStyle}>
			<div className={styles.yearNav}>
				<button
					type="button"
					className={styles.yearBtn}
					onClick={() => setDropdownYear((y) => y - 1)}
				>
					<KeyboardArrowLeftOutlined className={styles.yearNavIcon} />
				</button>
				<span className={styles.yearLabel}>{dropdownYear}</span>
				<button
					type="button"
					className={styles.yearBtn}
					onClick={() => setDropdownYear((y) => y + 1)}
				>
					<KeyboardArrowRightOutlined className={styles.yearNavIcon} />
				</button>
			</div>
			<div className={styles.monthGrid}>
				{MONTH_ABBR_PT.map((abbr, idx) => (
					<button
						key={abbr}
						type="button"
						className={`${styles.monthBtn} ${idx === month - 1 && dropdownYear === year ? styles.monthBtnSelected : ''}`}
						onMouseDown={() => handleMonthSelect(idx)}
					>
						{abbr}
					</button>
				))}
			</div>
		</div>
	) : null;

	return (
		<div className={styles.filterRow}>
			<button type="button" className={styles.arrowBtn} onClick={handlePrev}>
				<KeyboardArrowLeftOutlined className={styles.arrowIcon} />
			</button>

			<div
				ref={triggerRef}
				className={`${styles.calendarCard} ${isOpen ? styles.calendarCardOpen : ''}`}
				onClick={handleToggle}
			>
				<CalendarTodayOutlined className={styles.calendarIcon} />
				<span className={styles.calendarLabel}>
					{MONTH_NAMES_PT[month - 1]}, {year}
				</span>
				<KeyboardArrowDownOutlined
					className={`${styles.chevronIcon} ${isOpen ? styles.chevronOpen : ''}`}
				/>
			</div>

			<button type="button" className={styles.arrowBtn} onClick={handleNext}>
				<KeyboardArrowRightOutlined className={styles.arrowIcon} />
			</button>

			{typeof document !== 'undefined' && createPortal(dropdown, document.body)}
		</div>
	);
};
